import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import QRCode from 'qrcode';
import { PAIRING_TTL_SECONDS, type AuthResult } from '@aiscoreboard/contracts';
import { createPairing, pairingStatus } from '../lib/accounts';
import { useLocale } from '../lib/i18n';
import { pairUrl } from '../lib/handoff';

interface Props {
  onDone: (auth: AuthResult) => void;
  /**
   * Overrides the caption under a compact code. The setup screen puts one of
   * these in each seat, and two identical white squares side by side with the
   * same words under them read as the same code printed twice — which is the
   * one thing they are not. Naming the seat is what makes them two offers.
   */
  caption?: string;
  /**
   * Trims the panel down to the code itself, for when it sits inside a seat
   * card rather than filling a sign-in screen.
   */
  compact?: boolean;
  /**
   * Maximum side of the compact code in pixels. A code is scanned from a phone held a
   * foot away, so it only has to be as big as the card it sits in allows — a
   * narrow card gets a smaller square rather than a square that dictates how
   * wide the card must be. Setup also budgets for viewport height and scrolls
   * rather than reducing a code below its scanning floor.
   */
  compactSize?: number;
}

/** How often the scoreboard asks whether anybody has taken the code. */
const POLL_INTERVAL_MS = 1500;
const CREATE_RETRY_DELAY_MS = 1200;
const MAX_CREATE_RETRIES = 3;

/**
 * The scoreboard shows a QR code; a phone scans it and signs in there.
 *
 * This is the only route that works for a player who has no account yet: you
 * cannot show a sign-in code for an account that does not exist. Registering
 * means a name, a country, a club and a password, which nobody wants to type on
 * a shared tablet while their opponent watches, and it needs no camera on the
 * scoreboard — desktop Chrome on Windows cannot scan a QR code at all.
 */
export function PairPanel({ onDone, compact = false, compactSize = 251, caption }: Props) {
  const { locale, t } = useLocale();
  const [image, setImage] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [copied, setCopied] = useState(false);

  // Kept in a ref so the polling effect does not restart on every render.
  const done = useRef(onDone);
  done.current = onDone;

  const [attempt, setAttempt] = useState(0);
  const restart = useCallback(() => {
    setImage(null);
    setUrl(null);
    setError(null);
    setExpired(false);
    setCopied(false);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let createRetries = 0;

    const create = async (): Promise<void> => {
      try {
        const { code, pollSecret } = await createPairing();
        if (cancelled) return;

        const link = await pairUrl(code, locale);
        if (cancelled) return;
        setUrl(link);
        setImage(await QRCode.toDataURL(link, { width: 512, margin: 1 }));

        const poll = async (): Promise<void> => {
          if (cancelled) return;
          try {
            const status = await pairingStatus(pollSecret);
            if (cancelled) return;
            if (status.status === 'claimed') {
              done.current(status);
              return;
            }
          } catch (e: unknown) {
            // Only a real expiry should end this code. Temporary network or
            // gateway failures should keep polling, otherwise one blip makes
            // a fresh QR look "expired".
            const message = e instanceof Error ? e.message.toLowerCase() : '';
            if (message.includes('expired') || message.includes('used')) {
              if (!cancelled) setExpired(true);
              return;
            }
          }
          timer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
        };

        timer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
      } catch (e: unknown) {
        if (cancelled) return;
        createRetries += 1;
        if (createRetries <= MAX_CREATE_RETRIES) {
          timer = window.setTimeout(() => void create(), CREATE_RETRY_DELAY_MS);
          return;
        }
        // Friendly message only: upstream proxies sometimes return full HTML
        // bodies, which looked like red gibberish on screen.
        setError(e instanceof Error && e.message !== '' ? e.message : t('pair.failed'));
      }
    };

    void create();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [attempt, locale, t]);

  const copy = async (): Promise<void> => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <p className="text-center text-sm text-ball-red">{error}</p>
        <button type="button" onClick={restart} className="btn-ghost text-sm">
          {t('pair.retry')}
        </button>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <p className="text-center text-sm text-white/60">{t('pair.expired')}</p>
        <button type="button" onClick={restart} className="btn bg-white text-hud-bg">
          {t('pair.newCode')}
        </button>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="seat-pair flex w-full flex-col items-center gap-3" style={{ '--seat-code-max': `${compactSize}px` } as CSSProperties}>
        {image ? (
          <img
            src={image}
            alt={t('pair.alt')}
            className="seat-square rounded-lg bg-white p-2"
            width={compactSize}
          />
        ) : (
          <div
            className="seat-square grid place-items-center text-xs text-white/40"
          >
            {t('pair.generating')}
          </div>
        )}
        <p className="seat-caption text-center text-xs text-white/50">{caption ?? t('pair.short')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-center text-sm text-white/60">{t('pair.description')}</p>

      {image ? (
        <img src={image} alt={t('pair.alt')} className="rounded-xl bg-white p-2" width={230} />
      ) : (
        <p className="py-16 text-white/50">{t('pair.generating')}</p>
      )}

      {url && (
        <button
          type="button"
          onClick={() => void copy()}
          title={t('pair.copy')}
          className="w-full break-all rounded-xl bg-black/40 px-3 py-2 text-center font-mono text-[11px] text-white/60"
        >
          {url}
        </button>
      )}
      {copied && <p className="text-xs text-ball-green">{t('pair.copied')}</p>}

      <p className="text-xs text-white/40">
        {t('pair.waiting', { n: Math.round(PAIRING_TTL_SECONDS / 60) })}
      </p>
    </div>
  );
}
